import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    VideoCameraIcon,
    PlayIcon,
    CheckCircleIcon,
    ClockIcon,
    QuestionMarkCircleIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

export default function InternLearningModules() {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAssignedModules();
    }, []);

    const fetchAssignedModules = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/learning-modules/assigned');
            if (res.data.success) {
                setModules(res.data.data || []);
            }
        } catch (err) {
            toast.error('Failed to load assigned video modules');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full space-y-6 pb-12">
            <PageHeader
                title="Mentor Video Learning Modules"
                description="Interactive video lessons and quizzes assigned to you by your mentor. Complete videos to unlock and answer module questions."
            />

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="card p-5 space-y-4 animate-pulse">
                            <div className="aspect-video bg-ink-100 rounded-xl" />
                            <div className="h-4 bg-ink-200 rounded w-3/4" />
                        </div>
                    ))}
                </div>
            ) : modules.length === 0 ? (
                <EmptyState
                    icon={VideoCameraIcon}
                    title="No Learning Modules Assigned Yet"
                    description="When your mentor creates video learning modules for you, they will appear here."
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {modules.map((m) => {
                        const sub = m.submission;
                        const isCompleted = sub?.is_submitted;
                        const isVideoWatched = sub?.video_watched || isCompleted;

                        return (
                            <Link
                                key={m.id}
                                to={`/learning-modules/${m.id}`}
                                className="card p-5 border border-border hover:border-brand-300 hover:shadow-card-hover transition-all duration-200 flex flex-col justify-between group relative overflow-hidden"
                            >
                                <div className="space-y-3">
                                    {/* Thumbnail */}
                                    <div className="relative aspect-video rounded-xl overflow-hidden bg-ink-900 border border-border">
                                        <img
                                            src={`https://img.youtube.com/vi/${m.youtube_video_id}/hqdefault.jpg`}
                                            alt={m.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute inset-0 bg-ink-950/40 flex items-center justify-center group-hover:bg-ink-950/60 transition-colors">
                                            <div className="w-12 h-12 rounded-full bg-brand-700 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                <PlayIcon className="w-6 h-6 translate-x-0.5" />
                                            </div>
                                        </div>

                                        <div className="absolute top-2 right-2 flex items-center gap-1">
                                            {sub?.is_passed ? (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white shadow">
                                                    ✓ Passed ({sub.percentage}%)
                                                </span>
                                            ) : isCompleted ? (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-600 text-white shadow">
                                                    ✕ Failed ({sub.percentage}%)
                                                </span>
                                            ) : isVideoWatched ? (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white shadow">
                                                    Video Watched
                                                </span>
                                            ) : (
                                                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-brand-700 text-white shadow">
                                                    New Module
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.module_type === 'training' ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-sky-100 text-sky-900 border border-sky-300'}`}>
                                                {m.module_type === 'training' ? '🛠️ Training' : '📘 Learning'}
                                            </span>
                                            <span className="text-[10px] text-ink-500 font-semibold">
                                                ⏱️ {m.duration_minutes || 30} mins
                                            </span>
                                            <span className="text-[10px] text-emerald-700 font-bold">
                                                🎯 Pass: {m.passing_percentage !== undefined ? m.passing_percentage : 90}%
                                            </span>
                                        </div>
                                        <h3 className="font-bold text-ink-950 group-hover:text-brand-700 transition-colors line-clamp-1">
                                            {m.title}
                                        </h3>
                                        <p className="text-xs text-ink-500 mt-1 line-clamp-2">
                                            {m.description || 'No description provided.'}
                                        </p>
                                    </div>

                                    {/* Metadata Pills */}
                                    <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
                                        <span className="px-2 py-0.5 bg-brand-50 text-brand-800 border border-brand-200 rounded text-[11px] font-semibold flex items-center gap-1">
                                            <QuestionMarkCircleIcon className="w-3.5 h-3.5" />
                                            {m.question_count} Questions
                                        </span>
                                        {m.mentor && (
                                            <span className="text-ink-400 text-[11px]">
                                                By {m.mentor.first_name} {m.mentor.last_name}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs font-semibold text-brand-700 group-hover:translate-x-1 transition-transform">
                                    <span>{isCompleted ? 'View Results' : 'Watch Video & Take Quiz'}</span>
                                    <PlayIcon className="w-4 h-4" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
